type LocationLike = {
  protocol: string;
  hostname: string;
  port?: string;
};

function getLocationLike(locationOverride?: LocationLike): LocationLike | null {
  if (locationOverride) {
    return locationOverride;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  return {
    protocol: window.location.protocol,
    hostname: window.location.hostname,
    port: window.location.port,
  };
}

function normalizeHostname(hostname: string): string {
  return hostname.trim().toLowerCase();
}

export function isLocalFrontendHost(hostname: string): boolean {
  const normalizedHost = normalizeHostname(hostname);

  return normalizedHost === 'localhost'
    || normalizedHost === '127.0.0.1'
    || normalizedHost.endsWith('.localhost')
    || normalizedHost.endsWith('.localtest.me');
}

function inferHostedEnvironment(hostname: string): 'dev' | 'prod' {
  const normalizedHost = normalizeHostname(hostname);

  return normalizedHost.endsWith('.dev.modalhub.net') || normalizedHost === 'api.dev.modalhub.net'
    ? 'dev'
    : 'prod';
}

function buildLocalTenantHost(hostname: string, slug: string): string {
  const normalizedHost = normalizeHostname(hostname);

  if (normalizedHost === 'localhost' || normalizedHost === '127.0.0.1') {
    return normalizedHost;
  }

  const parts = normalizedHost.split('.').filter(Boolean);
  if (parts.length < 2) {
    return normalizedHost;
  }

  return [slug, ...parts.slice(1)].join('.');
}

function buildTemplateUrl(template: string, slug: string): string | null {
  if (!template.includes('{slug}')) {
    return null;
  }

  const resolved = template.replaceAll('{slug}', slug);

  try {
    const parsed = resolved.startsWith('http://') || resolved.startsWith('https://')
      ? new URL(resolved)
      : new URL(`https://${resolved}`);

    return `${parsed.protocol}//${parsed.host}`;
  } catch {
    return null;
  }
}

export function buildTenantAppBaseUrl(slug: string, locationOverride?: LocationLike): string {
  const template = process.env.REACT_APP_FRONTEND_URL_TEMPLATE;
  const templateUrl = template ? buildTemplateUrl(template, slug) : null;
  if (templateUrl) {
    return templateUrl;
  }

  const locationLike = getLocationLike(locationOverride);
  if (!locationLike) {
    return `https://${slug}.modalhub.net`;
  }

  if (isLocalFrontendHost(locationLike.hostname)) {
    const localHost = buildLocalTenantHost(locationLike.hostname, slug);
    const port = locationLike.port ? `:${locationLike.port}` : '';

    return `${locationLike.protocol}//${localHost}${port}`;
  }

  const environment = inferHostedEnvironment(locationLike.hostname);
  const suffix = environment === 'dev' ? 'dev.modalhub.net' : 'modalhub.net';
  return `https://${slug}.${suffix}`;
}

export function buildGlobalAdminBaseUrl(locationOverride?: LocationLike): string {
  return buildTenantAppBaseUrl('globaladmin', locationOverride);
}

export function getResolvedApiBaseUrl(locationOverride?: LocationLike): string {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  const locationLike = getLocationLike(locationOverride);
  if (!locationLike || isLocalFrontendHost(locationLike.hostname)) {
    return '/api';
  }

  return inferHostedEnvironment(locationLike.hostname) === 'dev'
    ? 'https://api.dev.modalhub.net'
    : 'https://api.modalhub.net';
}
